import { Component, Input } from '@angular/core';
import * as d3 from 'd3';

@Component({
  selector: 'app-cogging-torque-compensation-graph',
  templateUrl: './cogging-torque-compensation-graph.component.html',
})
export class CoggingTorqueCompensationGraphComponent {
  @Input() enabled: boolean;

  actualPositionToCoggingTorqueCompensationLinearScale: d3.ScaleLinear<number, number>;

  circles = 5;
  radiusOffset = 100;
  minLimit = -150;
  maxLimit = 150;

  private _data: number[];
  private _position: number;
  private _resolution: number;

  get g() {
    return d3.select('.cogging-torque-compensation-graph > g');
  }
  get data() {
    return this._data;
  }

  @Input()
  set data(data: number[]) {
    this._data = data;
    this.clear();
    this.draw();
    if (this.isDataValid && this.resolution) {
      this.drawActualPositionLine(this.position);
      this.drawCoggingTorqueCompensationValueAtActualPosition(this.position);
    }
  }

  get position() {
    return this._position;
  }

  @Input()
  set position(position: number) {
    this._position = position;

    if (this.resolution) {
      this.drawActualPositionLine(this.position);
      if (this.isDataValid) {
        this.drawCoggingTorqueCompensationValueAtActualPosition(this.position);
      }
    }
  }

  get resolution() {
    return this._resolution;
  }

  @Input()
  set resolution(resolution: number) {
    this._resolution = resolution;
    if (resolution) {
      this.actualPositionToCoggingTorqueCompensationLinearScale = d3
        .scaleLinear()
        .domain([0, this.resolution])
        .range([0, 1023]);
    }
  }

  get divider() {
    return Math.abs(this.maxLimit - this.minLimit) / (this.circles - 1); // 75
  }

  get reference() {
    return this.radiusOffset + Math.abs(this.maxLimit - this.minLimit) / 2; // 250
  }

  get valueRange() {
    const valueRange = []; // [-150, -75, 0, 75, 150]
    for (let i = 0; i < this.circles; i++) {
      const value = this.minLimit + i * this.divider;
      valueRange.push(value);
    }
    return valueRange;
  }

  get circleRadiuses() {
    const circleRadiuses = []; // [100, 175, 250, 325, 400]
    for (let i = 0; i < this.circles; i++) {
      const r = this.radiusOffset + i * this.divider;
      circleRadiuses.push(r);
    }
    return circleRadiuses;
  }

  get minValue() {
    return Math.min(...this.data);
  }

  get maxValue() {
    return Math.max(...this.data);
  }

  get isDataValid() {
    return this.data?.length === 1024;
  }

  gradValue(resolution: number = 1024) {
    const grad = Math.PI / 180; // 0.017453292519943295
    return (360 / resolution) * grad; // 0.006135923151542565
  }

  clear() {
    d3.selectAll('.cogging-torque-compensation-graph g > *').remove();
  }

  draw() {
    const g = this.g;

    this.circleRadiuses.forEach((r) => g.append('circle').attr('class', 'concentric-circle').attr('r', r));

    const axisGrid = g.append('g').attr('class', 'axisWrapper');

    axisGrid
      .append('line')
      .attr('class', 'line')
      .attr('x1', 0)
      .attr('y1', -this.circleRadiuses[0])
      .attr('x2', 0)
      .attr('y2', -this.circleRadiuses[this.circles - 1])
      .style('stroke', '#dadada')
      .style('stroke-width', '1pt');

    axisGrid
      .selectAll('.axisLabel')
      .data(this.valueRange)
      .enter()
      .append('text')
      .attr('class', 'axisLabel')
      .attr('x', 8)
      .attr('y', (d) => -d - this.reference)
      .attr('dy', '0.4em')
      .attr('fill', '#777777')
      .style('font-size', '11pt')
      .text((d) => d + ' ‰');

    // filter for the outside glow
    const filter = g.append('defs').append('filter').attr('id', 'glow');
    filter.append('feGaussianBlur').attr('stdDeviation', '2.5').attr('result', 'coloredBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    if (!this.isDataValid) {
      return;
    }

    const radialLineGenerator = d3.lineRadial();

    const radialLineData = [];
    let minValueRadialLineData = [];
    let maxValueRadialLineData = [];
    for (let i = 0; i < this.data.length; i++) {
      const angle = this.gradValue(this.data.length) * i;
      const radius = this.reference + this.data[i];
      radialLineData.push([angle, radius]);
      if (this.data[i] === this.minValue) {
        minValueRadialLineData = radialLineData[radialLineData.length - 1];
      }
      if (this.data[i] === this.maxValue) {
        maxValueRadialLineData = radialLineData[radialLineData.length - 1];
      }
    }

    const radialLine = radialLineGenerator(radialLineData);

    g.append('path').attr('d', radialLine);

    g.append('circle').attr('class', 'max-value-circle').attr('r', maxValueRadialLineData[1]);
    g.append('circle').attr('class', 'min-value-circle').attr('r', minValueRadialLineData[1]);

    let cx: number;
    let cy: number;
    let maxAbsValue: number;
    if (Math.abs(this.maxValue) > Math.abs(this.minValue)) {
      cy = maxValueRadialLineData[1] * Math.cos(maxValueRadialLineData[0]);
      cx = maxValueRadialLineData[1] * Math.sin(maxValueRadialLineData[0]);
      maxAbsValue = Math.abs(this.maxValue);
    } else {
      cy = minValueRadialLineData[1] * Math.cos(minValueRadialLineData[0]);
      cx = minValueRadialLineData[1] * Math.sin(minValueRadialLineData[0]);
      maxAbsValue = Math.abs(this.minValue);
    }

    g.append('circle').attr('class', 'max-abs-value-circle').attr('r', 16).attr('cx', cx).attr('cy', -cy);
    g.append('text')
      .attr('x', cx + 20)
      .attr('y', -cy + 20)
      .attr('dy', '0.4em')
      .attr('fill', '#777777')
      .style('font-size', '11pt')
      .text(() => `Max: ${maxAbsValue.toFixed(2)} ‰`);
  }

  drawActualPositionLine(actualPosition: number) {
    const p = actualPosition % this.resolution;
    const angle = this.gradValue(this.resolution) * p;
    const cy = Math.cos(angle);
    const cx = Math.sin(angle);

    this.g.select('.actual-position-line').remove();
    this.g
      .append('line')
      .attr('class', 'actual-position-line')
      .style('stroke', '#777777')
      .style('stroke-width', '1.5pt')
      .attr('x1', this.circleRadiuses[0] * cx)
      .attr('y1', -(this.circleRadiuses[0] * cy))
      .attr('x2', this.circleRadiuses[this.circleRadiuses.length - 1] * cx)
      .attr('y2', -(this.circleRadiuses[this.circleRadiuses.length - 1] * cy));
  }

  drawCoggingTorqueCompensationValueAtActualPosition(actualPosition: number) {
    let p = actualPosition % this.resolution;
    if (p < 0) {
      p = this.resolution + p;
    }
    const index = Math.floor(this.actualPositionToCoggingTorqueCompensationLinearScale(p));
    const value = this.data[index];
    const text = `${value.toFixed(2)} ‰`;

    this.g.select('.compensation-value-text').remove();
    this.g
      .append('text')
      .attr('class', 'compensation-value-text')
      .attr('x', 0)
      .attr('dx', 10)
      .attr('y', 0)
      .attr('fill', '#777777')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .style('font-size', '22pt')
      .style('font-weight', '300')
      .text(() => text);
  }
}
